/**
 * Protected Route Component
 * 
 * Wraps routes that require authentication and optionally specific roles.
 * Redirects unauthenticated users to signup and admin-only pages to appropriate routes.
 */

import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { UserRole } from '@/lib/adapters/trackingeats';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireRole?: UserRole;
  redirectTo?: string;
}

export const ProtectedRoute = ({ 
  children, 
  requireRole,
  redirectTo = '/signup'
}: ProtectedRouteProps) => {
  const { user, loading } = useUser();

  // Show loading indicator while checking auth state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Redirect to signup if not authenticated
  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check role requirement if specified
  if (requireRole && user.role !== requireRole) {
    // Redirect based on role
    if (requireRole === 'admin') {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to={redirectTo} replace />;
  }

  // User is authenticated and has required role (if specified)
  return <>{children}</>;
};

